import { ComponentPropsWithoutRef, PropsWithChildren, ReactNode } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NucleoIcon, NucleoIconProps } from 'components/nucleo-icon';
import { IconName } from 'components/nucleo-icon/icons';
import { useCloseSidebar } from 'customHooks/useCloseSidebar';
import { twMerge } from 'tailwind-merge';

interface SidebarLinkProps extends LinkProps {}
const SidebarLink = ({ to, children, onClick, className, ...restProps }: SidebarLinkProps) => {
    const closeSidebar = useCloseSidebar();
    return (
        <Link
            to={to}
            className={twMerge('block', className)}
            onClick={e => {
                onClick?.(e);
                closeSidebar();
            }}
            {...restProps}
        >
            {children}
        </Link>
    );
};

interface LevelOneItemProps {
    icon: IconName;
    iconProps?: Omit<NucleoIconProps, 'icon'>;
    isToggle?: boolean;
    isActive?: boolean;
    rightAdornment?: ReactNode;
}
const LevelOneItem = ({
    icon,
    iconProps,
    isToggle = false,
    isActive = false,
    rightAdornment,
    children,
}: PropsWithChildren<LevelOneItemProps>) => {
    const { className: iconClassName, ...restIconProps } = iconProps || {};
    return (
        <div
            className={twMerge(
                'group/menu flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-lg bg-transparent px-3 py-2 hover:bg-white',
                isActive && 'bg-white',
            )}
        >
            <NucleoIcon
                icon={icon}
                className={twMerge(
                    'flex-shrink-0 text-gray-700',
                    isActive && 'text-blue-500',
                    iconClassName,
                )}
                {...restIconProps}
            />

            <span className="min-w-0 flex-grow justify-start text-left text-sm font-medium leading-4 text-gray-700">
                {children}
            </span>
            {isToggle ? (
                <div className="-mb-1 -mt-1 flex h-6 w-6 items-center justify-center">
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        size="sm"
                        className="group-radix-state-open:rotate-180 text-gray-600 transition-transform"
                    />
                </div>
            ) : null}
            {rightAdornment || null}
        </div>
    );
};

interface LevelTwoItemProps {
    isActive?: boolean;
    rightAdornment?: ReactNode;
}
const LevelTwoItem = ({
    children,
    isActive = false,
    rightAdornment,
}: PropsWithChildren<LevelTwoItemProps>) => {
    return (
        <div
            className={twMerge(
                'group/menu grid cursor-pointer grid-cols-[16px_1fr_24px] items-center gap-2 rounded-lg bg-transparent p-2 hover:bg-gray-100',
                isActive && 'bg-gray-100',
            )}
        >
            <span className="flex w-4 flex-col items-center">
                <span className="-mt-2 h-[13.5px] w-px bg-gray-200 group-first/list-item:bg-transparent" />
                <span className="h-[5px] w-[5px] rounded-full bg-gray-200" />
                <span className="-mb-2 h-[13.5px] w-px bg-gray-200 group-last/list-item:bg-transparent" />
            </span>
            <span className="min-w-0 truncate text-sm font-normal leading-4 text-gray-600">
                {children}
            </span>
            {rightAdornment || null}
        </div>
    );
};

interface SidebarLinkGroupProps extends ComponentPropsWithoutRef<'div'> {}
const SidebarLinkGroup = (props: SidebarLinkGroupProps) => {
    const { children, className = '', ...rest } = props;
    return (
        <div className={twMerge('flex flex-col gap-0.5', className)} {...rest}>
            {children}
        </div>
    );
};

const LevelOne = LevelOneItem;
const LevelTwo = LevelTwoItem;
const Group = SidebarLinkGroup;

export {
    SidebarLink as Link,
    LevelOne,
    LevelTwo,
    Group,
    //
    LevelOneItem,
    LevelTwoItem,
    SidebarLink,
    SidebarLinkGroup,
};
export type { LevelOneItemProps, LevelTwoItemProps, SidebarLinkProps };
